import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Stack,
  CircularProgress,
  Alert,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Divider,
  Paper,
  FormControl,
  Select,
  MenuItem,
  Link,
  useTheme,
} from "@mui/material";
import LanguageIcon from "@mui/icons-material/Language";
import { SUPPORTED_LANGUAGES } from "../contexts/LanguageContext";
import { useTranslations } from "../utils/translation";
import { alpha } from "@mui/material/styles";
import noticesService from "../services/notices-service";
import { languageService, Language } from "../services/language-service";
import { config } from "../config/config";

interface ConsentNoticeFormProps {
  collectionPointId: number;
  onSuccess?: () => void;
  onError?: (error: string) => void;
  onLanguagesDetected?: (languages: string[]) => void; // Callback to notify parent of available languages
  externalLanguageCode?: string; // External language selection from parent (for header picker)
  /** Client subdomain for policy links (Privacy Policy, Contact DPO, Your Rights) */
  subdomain?: string;
  /** When false, form is read-only until effective date (portal gating). */
  formDisabled?: boolean;
  formDisabledMessage?: string;
}

interface NoticeConfig {
  titleLabelId: number;
  subtitleLabelId?: number | null;
  descriptionLabelId?: number | null;
  submitButtonLabelId: number;
  dataElementOrder: number[];
  purposeOrder: number[];
}

interface SnapshotLabel {
  id: number;
  key: string;
  name: string;
  description?: string;
  translations: Array<{
    languageCode: string;
    translatedName: string;
    translatedDescription?: string;
  }>;
}

interface SnapshotDataElement {
  id: number;
  key: string;
  name: string;
  description?: string;
  isRequired: boolean;
  type: {
    code: string;
    label: string;
  };
  translations: Array<{
    languageCode: string;
    translatedName: string;
    translatedDescription?: string;
  }>;
}

/** Retention info from snapshot (matches backend snapshot shape) */
interface SnapshotRetention {
  type?: { code?: string; label?: string };
  timeBasedPeriod?: number;
  timeBasedUnit?: { code?: string; label?: string };
  endConditions?: {
    onPurposeFulfilled?: boolean;
    onConsentWithdrawn?: boolean;
    onRetentionExpired?: boolean;
    onErasureRequested?: boolean;
    onAccountClosure?: boolean;
  };
}

interface SnapshotPurpose {
  id: number;
  key: string;
  name: string;
  description?: string;
  isRequired: boolean;
  lawfulGround: {
    code: string;
    label: string;
  };
  retention?: SnapshotRetention;
  translations: Array<{
    languageCode: string;
    translatedName: string;
    translatedDescription?: string;
  }>;
}

interface SnapshotStructure {
  collectionPoint: {
    id: number;
    name: string;
    description?: string;
    identifierDataElementId?: number;
    identifierDataElementKey?: string;
  };
  labels: SnapshotLabel[];
  dataElements: SnapshotDataElement[];
  purposes: SnapshotPurpose[];
}

const ConsentNoticeForm: React.FC<ConsentNoticeFormProps> = ({
  collectionPointId,
  onSuccess,
  onError,
  onLanguagesDetected,
  externalLanguageCode,
  subdomain,
  formDisabled = false,
  formDisabledMessage,
}) => {
  const theme = useTheme();
  const [loading, setLoading] = useState<boolean>(true);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [snapshot, setSnapshot] = useState<SnapshotStructure | null>(null);
  const [noticeConfig, setNoticeConfig] = useState<NoticeConfig | null>(null);
  const [languageCode, setLanguageCode] = useState<string>("en");
  const [uiLanguage, setUiLanguage] = useState<string>("en"); // For UI labels translation
  const [availableLanguages, setAvailableLanguages] = useState<string[]>([]);
  const [allSupportedLanguages, setAllSupportedLanguages] = useState<Language[]>([]);
  const t = useTranslations(uiLanguage);

  // Form state
  const [formData, setFormData] = useState<Record<number, any>>({});
  const [purposeConsents, setPurposeConsents] = useState<Record<number, boolean>>({});
  const [fieldErrors, setFieldErrors] = useState<Record<number, string>>({}); // Field-level errors: elementId or purposeId -> error message

  useEffect(() => {
    console.log("ConsentNoticeForm - useEffect triggered", { collectionPointId });
    // Only load form data if collectionPointId is valid
    if (collectionPointId && collectionPointId > 0) {
      console.log("ConsentNoticeForm - collectionPointId is valid, calling loadFormData");
      loadFormData();
    } else {
      console.warn("ConsentNoticeForm - Invalid collectionPointId:", collectionPointId);
      setLoading(false);
      setError("Invalid collection point ID");
    }
  }, [collectionPointId]);

  // Sync with external language selection (from header picker)
  useEffect(() => {
    if (externalLanguageCode && externalLanguageCode !== languageCode) {
      console.log("ConsentNoticeForm - Syncing language from external:", externalLanguageCode);
      setLanguageCode(externalLanguageCode);
      setUiLanguage(externalLanguageCode);
    }
  }, [externalLanguageCode]);

  const loadFormData = async () => {
    console.log("ConsentNoticeForm - loadFormData called", { collectionPointId });

    // Guard: ensure collectionPointId is valid before making API calls
    if (!collectionPointId || collectionPointId <= 0) {
      console.error("ConsentNoticeForm - Invalid collectionPointId in loadFormData:", collectionPointId);
      setError("Invalid collection point ID");
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      console.log("ConsentNoticeForm - Making API calls for collectionPointId:", collectionPointId);
      console.log("ConsentNoticeForm - API URLs:", {
        snapshot: `/portal/collection-points/${collectionPointId}/snapshot`,
        config: `/portal/collection-points/${collectionPointId}/notice-config`,
      });

      // Load snapshot, notice config, and all supported languages in parallel
      const [snapshotData, configData, allLanguages] = await Promise.all([
        noticesService.getCollectionPointSnapshot(collectionPointId),
        noticesService.getCollectionPointNoticeConfig(collectionPointId),
        languageService.getSupportedLanguages().catch((err) => {
          console.warn("ConsentNoticeForm - Failed to fetch all languages, using fallback:", err);
          // Fallback to English if API fails
          return [languageService.getDefaultLanguage()];
        }),
      ]);

      console.log("ConsentNoticeForm - API calls successful", { snapshotData, configData, allLanguages });

      setSnapshot(snapshotData);
      setNoticeConfig(configData);
      setAllSupportedLanguages(allLanguages);

      // Set available languages to ALL supported languages (not just detected ones)
      const allLangCodes = allLanguages.map((lang) => lang.code);
      console.log("ConsentNoticeForm - All supported languages:", allLangCodes);
      setAvailableLanguages(allLangCodes);

      // Notify parent component of available languages
      if (onLanguagesDetected) {
        onLanguagesDetected(allLangCodes);
      }

      // Set default language: prefer English, otherwise first available
      let defaultLang = "en";
      if (allLangCodes.includes("en")) {
        defaultLang = "en";
      } else if (allLangCodes.length > 0) {
        defaultLang = allLangCodes[0];
      }
      console.log("ConsentNoticeForm - Setting default language:", defaultLang);
      setLanguageCode(defaultLang);
      setUiLanguage(defaultLang); // Sync UI language with content language

      // Initialize form data
      if (snapshotData?.dataElements) {
        const initialData: Record<number, any> = {};
        console.log("📋 [ConsentNoticeForm] Initializing form data with data elements:", snapshotData.dataElements.map((e: SnapshotDataElement) => ({
          id: e.id,
          name: e.name,
          key: e.key,
          type: e.type?.code,
          typeLabel: e.type?.label,
          typeFull: e.type,
          isRequired: e.isRequired,
          fullElement: e
        })));
        console.log("📋 [ConsentNoticeForm] Full snapshot dataElements:", JSON.stringify(snapshotData.dataElements, null, 2));
        snapshotData.dataElements.forEach((element: SnapshotDataElement) => {
          initialData[element.id] = "";
        });
        setFormData(initialData);
      }

      // Initialize purpose consents
      if (snapshotData?.purposes) {
        const initialConsents: Record<number, boolean> = {};
        snapshotData.purposes
          .filter((p: SnapshotPurpose) => p.lawfulGround.code === "CONSENT")
          .forEach((purpose: SnapshotPurpose) => {
            initialConsents[purpose.id] = false;
          });
        setPurposeConsents(initialConsents);
      }
    } catch (err: any) {
      console.error("=== ConsentNoticeForm - Error loading form data ===");
      console.error("Error object:", err);
      console.error("Error message:", err?.message);
      console.error("Error response:", err?.response);
      console.error("Error status:", err?.response?.status);
      console.error("Error data:", err?.response?.data);
      console.error("CollectionPointId used:", collectionPointId);
      console.error("==================================================");

      const errorMessage = err?.message || "Failed to load consent form";
      setError(errorMessage);
      if (onError) {
        onError(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  const getLabelById = (labelId: number | null | undefined): string => {
    if (!labelId || !snapshot?.labels) return "";
    const label = snapshot.labels.find((l) => l.id === labelId);
    if (!label) return "";

    // Find translation for current language, fallback to English, then to name
    const translation = label.translations?.find((t) => t.languageCode === languageCode);
    if (translation) return translation.translatedName;

    const enTranslation = label.translations?.find((t) => t.languageCode === "en");
    if (enTranslation) return enTranslation.translatedName;

    return label.name;
  };

  const getDataElementLabel = (element: SnapshotDataElement): string => {
    const translation = element.translations?.find((t) => t.languageCode === languageCode);
    if (translation) return translation.translatedName;

    const enTranslation = element.translations?.find((t) => t.languageCode === "en");
    if (enTranslation) return enTranslation.translatedName;

    return element.name;
  };

  const getDataElementDescription = (element: SnapshotDataElement): string | undefined => {
    const translation = element.translations?.find((t) => t.languageCode === languageCode);
    if (translation?.translatedDescription) return translation.translatedDescription;

    const enTranslation = element.translations?.find((t) => t.languageCode === "en");
    if (enTranslation?.translatedDescription) return enTranslation.translatedDescription;

    return element.description;
  };

  const getPurposeLabel = (purpose: SnapshotPurpose): string => {
    const translation = purpose.translations?.find((t) => t.languageCode === languageCode);
    if (translation) return translation.translatedName;

    const enTranslation = purpose.translations?.find((t) => t.languageCode === "en");
    if (enTranslation) return enTranslation.translatedName;

    return purpose.name;
  };

  const getPurposeDescription = (purpose: SnapshotPurpose): string | undefined => {
    const translation = purpose.translations?.find((t) => t.languageCode === languageCode);
    if (translation?.translatedDescription) return translation.translatedDescription;

    const enTranslation = purpose.translations?.find((t) => t.languageCode === "en");
    if (enTranslation?.translatedDescription) return enTranslation.translatedDescription;

    return purpose.description;
  };

  /** Format retention from snapshot for display (time-based and/or end conditions). Uses t() for locale. */
  const formatRetentionSummary = (purpose: SnapshotPurpose): string | null => {
    const r = purpose.retention;
    if (!r) return null;
    const parts: string[] = [];
    const typeCode = (r.type?.code || "").toUpperCase();
    if (typeCode === "TIME_BASED" && r.timeBasedPeriod != null && r.timeBasedUnit) {
      const code = (r.timeBasedUnit.code || "").toUpperCase();
      const one = r.timeBasedPeriod === 1;
      let unit: string;
      if (code === "YEARS") unit = one ? t("retentionUnitYear") : t("retentionUnitYears");
      else if (code === "MONTHS") unit = one ? t("retentionUnitMonth") : t("retentionUnitMonths");
      else if (code === "DAYS") unit = one ? t("retentionUnitDay") : t("retentionUnitDays");
      else unit = r.timeBasedUnit.label?.toLowerCase() ?? "";
      if (unit) parts.push(`${r.timeBasedPeriod} ${unit}`);
    } else if (r.type?.label) {
      parts.push(r.type.label);
    }
    const ec = r.endConditions;
    if (ec) {
      if (ec.onConsentWithdrawn) parts.push(t("retentionUntilConsentWithdrawn"));
      if (ec.onPurposeFulfilled) parts.push(t("retentionUntilPurposeFulfilled"));
      if (ec.onRetentionExpired) parts.push(t("retentionUntilRetentionExpires"));
      if (ec.onErasureRequested) parts.push(t("retentionUntilErasureRequested"));
      if (ec.onAccountClosure) parts.push(t("retentionUntilAccountClosure"));
    }
    if (parts.length === 0) return null;
    return parts.join("; ");
  };

  const getLanguageName = (code: string): string => {
    // First try to find in allSupportedLanguages (from API)
    const lang = allSupportedLanguages.find((l) => l.code === code);
    if (lang) {
      return lang.localeName || lang.name || code.toUpperCase();
    }
    // Fallback to SUPPORTED_LANGUAGES (hardcoded)
    const fallbackLang = SUPPORTED_LANGUAGES.find((l) => l.code === code);
    return fallbackLang ? fallbackLang.nativeName : code.toUpperCase();
  };

  const handleLanguageChange = (event: any) => {
    const newLang = event.target.value;
    setLanguageCode(newLang);
    setUiLanguage(newLang); // Sync UI language with content language
  };

  // Helper function to validate a single field
  const validateField = (elementId: number, value: any): string | null => {
    console.log("🔍 [validateField] Called", { elementId, value, valueType: typeof value });
    
    if (!snapshot) {
      console.log("❌ [validateField] No snapshot available");
      return null;
    }
    
    const element = snapshot.dataElements.find((e) => e.id === elementId);
    if (!element) {
      console.log("❌ [validateField] Element not found", { elementId, availableIds: snapshot.dataElements.map(e => e.id) });
      return null;
    }

    // Log the full element structure to see what we're working with
    console.log("🔍 [validateField] Full element object:", JSON.stringify(element, null, 2));
    console.log("🔍 [validateField] Element.type:", element.type);
    console.log("🔍 [validateField] Element.type?.code:", element.type?.code);
    console.log("🔍 [validateField] Element keys:", Object.keys(element));

    // Try multiple ways to access the type (API returns dataType as string, not type.code)
    const elementAny = element as any;
    let elementType: string | null = null;
    
    // First check the standard interface property
    if (element.type?.code) {
      elementType = element.type.code.toUpperCase();
    } 
    // Then check dataType (this is what the API actually returns)
    else if (elementAny.dataType && typeof elementAny.dataType === 'string') {
      elementType = elementAny.dataType.toUpperCase();
    }
    // Fallback checks for other possible structures
    else if (elementAny.dataElementType?.code) {
      elementType = elementAny.dataElementType.code.toUpperCase();
    } else if (elementAny.dataElementType && typeof elementAny.dataElementType === 'string') {
      elementType = elementAny.dataElementType.toUpperCase();
    } else if (elementAny.typeCode) {
      elementType = String(elementAny.typeCode).toUpperCase();
    }
    
    // Validation regexes and patterns
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/; // 5 letters, 4 digits, 1 letter
    const pinRegex = /^[0-9]{6}$/; // 6 digits
    const aadharRegex = /^[0-9]{12}$/; // 12 digits
    const phoneRegex = /^[0-9]{10}$/; // 10 digits (Indian phone)
    const numberRegex = /^-?\d+(\.\d+)?$/; // Integer or decimal number
    const dateRegex = /^\d{4}-\d{2}-\d{2}$/; // YYYY-MM-DD format
    
    console.log("📋 [validateField] Element details", {
      elementId,
      elementName: element.name,
      elementKey: element.key,
      elementType,
      elementTypeCode: element.type?.code,
      elementTypeRaw: element.type,
      isRequired: element.isRequired,
      value,
      valueTrimmed: typeof value === "string" ? value.trim() : value,
      fullElement: element
    });

    // Check if value is empty for required fields
    if (element.isRequired) {
      if (!value || (typeof value === "string" && value.trim() === "")) {
        const errorMsg = `Please fill in required field: ${getDataElementLabel(element)}`;
        console.log("⚠️ [validateField] Required field empty", { elementId, errorMsg });
        return errorMsg;
      }
    }

    // Validate format based on data element type (only if value is not empty)
    if (value && typeof value === "string" && value.trim() !== "") {
      const trimmedValue = value.trim();
      let isValid = false;
      let errorMsg = "";

      switch (elementType) {
        case "EMAIL":
        case "EMAIL_ADDRESS":
          isValid = emailRegex.test(trimmedValue);
          errorMsg = `Please enter a valid email address for: ${getDataElementLabel(element)}`;
          console.log("📧 [validateField] Email validation", {
            elementId,
            elementType,
            trimmedValue,
            regexTest: isValid
          });
          break;

        case "PHONE":
        case "PHONE_NUMBER":
          // Remove any non-digit characters for validation
          const digitsOnly = trimmedValue.replace(/\D/g, '');
          isValid = phoneRegex.test(digitsOnly);
          errorMsg = `Please enter a valid 10-digit phone number for: ${getDataElementLabel(element)}`;
          console.log("📱 [validateField] Phone validation", {
            elementId,
            elementType,
            value: trimmedValue,
            digitsOnly,
            digitsLength: digitsOnly.length,
            isValid
          });
          break;

        case "PAN":
          // PAN format: 5 uppercase letters, 4 digits, 1 uppercase letter
          const panUpper = trimmedValue.toUpperCase();
          isValid = panRegex.test(panUpper);
          errorMsg = `Please enter a valid PAN number (format: ABCDE1234F) for: ${getDataElementLabel(element)}`;
          console.log("🆔 [validateField] PAN validation", {
            elementId,
            elementType,
            value: trimmedValue,
            panUpper,
            isValid
          });
          break;

        case "PIN":
        case "PINCODE":
          // PIN code: 6 digits
          isValid = pinRegex.test(trimmedValue);
          errorMsg = `Please enter a valid 6-digit PIN code for: ${getDataElementLabel(element)}`;
          console.log("📍 [validateField] PIN validation", {
            elementId,
            elementType,
            value: trimmedValue,
            isValid
          });
          break;

        case "AADHAR":
        case "AADHAAR":
          // Aadhar: 12 digits
          const aadharDigits = trimmedValue.replace(/\D/g, '');
          isValid = aadharRegex.test(aadharDigits);
          errorMsg = `Please enter a valid 12-digit Aadhar number for: ${getDataElementLabel(element)}`;
          console.log("🆔 [validateField] Aadhar validation", {
            elementId,
            elementType,
            value: trimmedValue,
            aadharDigits,
            isValid
          });
          break;

        case "NUMBER":
        case "NUMERIC":
          // Number: integer or decimal
          isValid = numberRegex.test(trimmedValue);
          errorMsg = `Please enter a valid number for: ${getDataElementLabel(element)}`;
          console.log("🔢 [validateField] Number validation", {
            elementId,
            elementType,
            value: trimmedValue,
            isValid
          });
          break;

        case "DATE":
          // Date: YYYY-MM-DD format
          isValid = dateRegex.test(trimmedValue);
          if (isValid) {
            // Also validate that it's a valid date
            const dateObj = new Date(trimmedValue);
            isValid = dateObj instanceof Date && !isNaN(dateObj.getTime());
          }
          errorMsg = `Please enter a valid date (YYYY-MM-DD) for: ${getDataElementLabel(element)}`;
          console.log("📅 [validateField] Date validation", {
            elementId,
            elementType,
            value: trimmedValue,
            isValid
          });
          break;

        case "TEXT":
        case "SELECTION":
        case "COUNTRY":
        case "STATE":
          // These types don't need format validation (text is free-form, selection/country/state are dropdowns)
          isValid = true;
          console.log("ℹ️ [validateField] No format validation needed", { elementType });
          break;

        default:
          // Unknown type - skip validation
          isValid = true;
          console.log("ℹ️ [validateField] Unknown type, skipping format validation", { elementType });
          break;
      }

      if (!isValid && errorMsg) {
        console.log("❌ [validateField] Validation failed", { elementId, elementType, errorMsg });
        return errorMsg;
      }

      if (isValid) {
        console.log("✅ [validateField] Validation passed", { elementId, elementType });
      }
    } else {
      console.log("ℹ️ [validateField] Value is empty or not a string, skipping format validation", {
        value,
        valueType: typeof value,
        isEmpty: !value || (typeof value === "string" && value.trim() === "")
      });
    }

    console.log("✅ [validateField] No errors found");
    return null; // No error
  };

  const handleDataElementChange = (elementId: number, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [elementId]: value,
    }));
    // Clear error for this field when user starts typing (will be re-validated on blur)
    if (fieldErrors[elementId]) {
      setFieldErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[elementId];
        return newErrors;
      });
    }
  };

  const handleDataElementBlur = (elementId: number) => {
    console.log("🎯 [handleDataElementBlur] Called", { elementId });
    const value = formData[elementId];
    console.log("📝 [handleDataElementBlur] Current formData value", { elementId, value, allFormData: formData });
    
    const error = validateField(elementId, value);
    console.log("🔍 [handleDataElementBlur] Validation result", { elementId, error, hasError: !!error });
    
    setFieldErrors((prev) => {
      console.log("📊 [handleDataElementBlur] Current fieldErrors", { prev, elementId });
      const newErrors = { ...prev };
      if (error) {
        newErrors[elementId] = error;
        console.log("❌ [handleDataElementBlur] Setting error for field", { elementId, error, newErrors });
      } else {
        delete newErrors[elementId];
        console.log("✅ [handleDataElementBlur] Clearing error for field", { elementId, newErrors });
      }
      
      // Update general error message
      // If there's an error on this field, show it
      // If this field is now valid, show the first remaining error or clear if none
      if (error) {
        console.log("🚨 [handleDataElementBlur] Setting general error", { error });
        setError(error);
      } else {
        const remainingErrors = Object.values(newErrors);
        console.log("🔍 [handleDataElementBlur] Checking remaining errors", { remainingErrors, count: remainingErrors.length });
        if (remainingErrors.length > 0) {
          console.log("⚠️ [handleDataElementBlur] Setting first remaining error", { error: remainingErrors[0] });
          setError(remainingErrors[0]);
        } else {
          console.log("✅ [handleDataElementBlur] Clearing general error - no errors remaining");
          setError(null);
        }
      }
      
      console.log("📤 [handleDataElementBlur] Returning new errors", { newErrors });
      return newErrors;
    });
  };

  const handlePurposeConsentChange = (purposeId: number, checked: boolean) => {
    setPurposeConsents((prev) => ({
      ...prev,
      [purposeId]: checked,
    }));
    // Clear error for this purpose when user checks/unchecks
    if (fieldErrors[purposeId]) {
      setFieldErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[purposeId];
        return newErrors;
      });
    }
  };

  const validateForm = (): boolean => {
    if (!snapshot || !noticeConfig) {
      setError("Form configuration not available");
      return false;
    }

    const errors: Record<number, string> = {};
    let hasErrors = false;
    const identifierDataElementIdForValidation = snapshot.collectionPoint?.identifierDataElementId;

    // Validate required data elements using the shared validateField function
    // Skip the identifier element when hidden (backend injects it for consent notice)
    for (const elementId of noticeConfig.dataElementOrder) {
      if (identifierDataElementIdForValidation != null && elementId === identifierDataElementIdForValidation) {
        continue;
      }
      const value = formData[elementId];
      const error = validateField(elementId, value);
      if (error) {
        errors[elementId] = error;
        hasErrors = true;
      }
    }

    // Validate mandatory consent purposes (isRequired = true AND lawfulGround = CONSENT)
    for (const purposeId of noticeConfig.purposeOrder) {
      const purpose = snapshot.purposes.find((p) => p.id === purposeId);
      if (purpose) {
        // Check if purpose is required AND has CONSENT lawful ground
        const isConsentPurpose = purpose.lawfulGround?.code === "CONSENT";
        if (purpose.isRequired && isConsentPurpose) {
          if (!purposeConsents[purposeId]) {
            const errorMsg = `Please consent to required purpose: ${getPurposeLabel(purpose)}`;
            errors[purposeId] = errorMsg;
            hasErrors = true;
          }
        }
      }
    }

    // Set field-level errors
    setFieldErrors(errors);

    // Also set general error message if there are any errors
    if (hasErrors) {
      const firstError = Object.values(errors)[0];
      setError(firstError || "Please fill in all required fields");
    } else {
      setError(null);
    }

    return !hasErrors;
  };

  const handleSubmit = async () => {
    // Prevent duplicate submissions
    if (submitting) {
      console.warn("Submission already in progress, ignoring duplicate call");
      return;
    }

    if (formDisabled) {
      return;
    }

    if (!validateForm()) {
      return;
    }

    if (!collectionPointId || collectionPointId <= 0) {
      setError("Invalid collection point ID");
      return;
    }

    try {
      setSubmitting(true);
      setError(null);

      // Submit consent via API
      const result = await noticesService.submitNoticeConsent(collectionPointId, formData, purposeConsents, languageCode);

      console.log("Consent submitted successfully:", result);

      // Clear all field errors on successful submission
      setFieldErrors({});
      setError(null);

      if (onSuccess) {
        onSuccess();
      }
    } catch (err: any) {
      console.error("Error submitting form:", err);
      const errorMessage = err?.message || "Failed to submit consent form";
      setError(errorMessage);
      if (onError) {
        onError(errorMessage);
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error && !snapshot) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  if (!snapshot || !noticeConfig) {
    return (
      <Alert severity="warning" sx={{ mb: 2 }}>
        Form configuration not available
      </Alert>
    );
  }

  // Get ordered data elements; for consent notice (DP already known) hide the identifier field
  const identifierDataElementId = snapshot.collectionPoint?.identifierDataElementId;
  const orderedDataElements = noticeConfig.dataElementOrder
    .map((id) => snapshot.dataElements.find((e) => e.id === id))
    .filter((e): e is SnapshotDataElement => e !== undefined)
    .filter(
      (e) =>
        identifierDataElementId == null || e.id !== identifierDataElementId
    );

  // Get ordered purposes (only CONSENT lawful ground)
  const orderedPurposes = noticeConfig.purposeOrder
    .map((id) => snapshot.purposes.find((p) => p.id === id))
    .filter((p): p is SnapshotPurpose => p !== undefined && p.lawfulGround.code === "CONSENT");

  return (
    <Box
      sx={{
        p: 3,
        pointerEvents: formDisabled ? "none" : undefined,
        opacity: formDisabled ? 0.65 : 1,
      }}
    >
      {error && (
        <Alert severity="error" sx={{ mb: 2, pointerEvents: "auto" }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {formDisabled && formDisabledMessage && (
        <Alert severity="info" sx={{ mb: 2, pointerEvents: "auto" }}>
          {formDisabledMessage}
        </Alert>
      )}

      {/* Intro block: title + subtitle + description */}
      <Paper
        variant="outlined"
        sx={{
          p: 2.5,
          mb: 3,
          bgcolor: "grey.50",
          borderColor: "divider",
        }}
      >
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 1.5, color: "text.primary" }}>
          {getLabelById(noticeConfig.titleLabelId)}
        </Typography>
        {noticeConfig.subtitleLabelId && (
          <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 1.5, lineHeight: 1.5 }}>
            {getLabelById(noticeConfig.subtitleLabelId)}
          </Typography>
        )}
        {noticeConfig.descriptionLabelId && (
          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
            {getLabelById(noticeConfig.descriptionLabelId)}
          </Typography>
        )}
      </Paper>

      {/* DPDPA compliance notice – distinct callout */}
      <Paper
        variant="outlined"
        sx={{
          borderLeft: `4px solid ${theme.palette.primary.main}`,
          bgcolor: alpha(theme.palette.primary.main, theme.palette.mode === "light" ? 0.08 : 0.12),
          px: 2,
          py: 1.5,
          mb: 3,
          borderColor: "divider",
          borderLeftColor: theme.palette.primary.main,
        }}
      >
        <Typography
          variant="caption"
          sx={{
            fontWeight: 700,
            textTransform: "uppercase",
            letterSpacing: 0.8,
            color: "primary.main",
            display: "block",
            mb: 0.5,
          }}
        >
          {t("compliance")}
        </Typography>
        <Typography variant="body2" sx={{ fontWeight: 500, color: "text.primary", lineHeight: 1.5 }}>
          {t("dpdpaNotice")}
        </Typography>
      </Paper>

      <Divider sx={{ my: 3 }} />

      {/* Data Elements */}
      {orderedDataElements.length > 0 && (
        <Box sx={{ mb: 4 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2, color: "text.primary" }}>
            {t("information")}
          </Typography>
          <Stack spacing={3}>
            {orderedDataElements.map((element) => {
              const hasError = !!fieldErrors[element.id];
              const errorMessage = fieldErrors[element.id];
              const helperText = hasError ? errorMessage : getDataElementDescription(element);

              // Determine input type based on data element type
              // Try multiple ways to access the type (API returns dataType as string, not type.code)
              const elementAny = element as any;
              let elementType: string | null = null;
              
              // First check the standard interface property
              if (element.type?.code) {
                elementType = element.type.code.toUpperCase();
              } 
              // Then check dataType (this is what the API actually returns)
              else if (elementAny.dataType && typeof elementAny.dataType === 'string') {
                elementType = elementAny.dataType.toUpperCase();
              }
              // Fallback checks for other possible structures
              else if (elementAny.dataElementType?.code) {
                elementType = elementAny.dataElementType.code.toUpperCase();
              } else if (elementAny.dataElementType && typeof elementAny.dataElementType === 'string') {
                elementType = elementAny.dataElementType.toUpperCase();
              } else if (elementAny.typeCode) {
                elementType = String(elementAny.typeCode).toUpperCase();
              }
              
              // Determine input type and properties based on data element type
              const isEmail = elementType === "EMAIL" || elementType === "EMAIL_ADDRESS";
              const isPhone = elementType === "PHONE" || elementType === "PHONE_NUMBER";
              const isNumber = elementType === "NUMBER" || elementType === "NUMERIC";
              const isDate = elementType === "DATE";
              const isPan = elementType === "PAN";
              const isPin = elementType === "PIN" || elementType === "PINCODE";
              const isAadhar = elementType === "AADHAR" || elementType === "AADHAAR";
              
              // Determine HTML input type
              let inputType: string = "text";
              if (isEmail) inputType = "email";
              else if (isPhone) inputType = "tel";
              else if (isNumber) inputType = "number";
              else if (isDate) inputType = "date";
              
              // Determine maxLength based on type
              let maxLength: number | undefined = undefined;
              if (isPhone) maxLength = 10;
              else if (isPin) maxLength = 6;
              else if (isAadhar) maxLength = 12;
              else if (isPan) maxLength = 10;
              
              // Determine inputMode for mobile keyboards
              let inputMode: "text" | "numeric" | "email" | "tel" | undefined = undefined;
              if (isPhone || isPin || isAadhar || isNumber) inputMode = "numeric";
              else if (isEmail) inputMode = "email";
              else if (isPhone) inputMode = "tel";
              
              // Debug logging for element rendering
              console.log("🎨 [TextField Render] Rendering field", {
                elementId: element.id,
                elementName: element.name,
                elementKey: element.key,
                elementType,
                elementTypeCode: element.type?.code,
                elementTypeRaw: element.type,
                elementFull: element,
                isEmail,
                isPhone,
                isNumber,
                isDate,
                isPan,
                isPin,
                isAadhar,
                inputType,
                maxLength,
                hasError,
                currentValue: formData[element.id],
                hasOnBlur: true
              });
              
              return (
                <TextField
                  key={element.id}
                  label={getDataElementLabel(element)}
                  required={element.isRequired}
                  fullWidth
                  type={inputType}
                  value={formData[element.id] || ""}
                  onChange={(e) => {
                    let newValue = e.target.value;
                    
                    // Apply formatting/restrictions based on type
                    if (isPan) {
                      // PAN: Only allow alphanumeric, auto-uppercase
                      newValue = newValue.replace(/[^A-Za-z0-9]/g, '').toUpperCase();
                      if (newValue.length > 10) newValue = newValue.substring(0, 10);
                    } else if (isPin || isAadhar) {
                      // PIN/Aadhar: Only digits
                      newValue = newValue.replace(/\D/g, '');
                      if (isPin && newValue.length > 6) newValue = newValue.substring(0, 6);
                      if (isAadhar && newValue.length > 12) newValue = newValue.substring(0, 12);
                    } else if (isPhone) {
                      // Phone: Only digits
                      newValue = newValue.replace(/\D/g, '');
                      if (newValue.length > 10) newValue = newValue.substring(0, 10);
                    }
                    
                    console.log("✏️ [TextField onChange] Called", { 
                      elementId: element.id, 
                      originalValue: e.target.value,
                      newValue,
                      elementType 
                    });
                    handleDataElementChange(element.id, newValue);
                  }}
                  onBlur={() => {
                    console.log("👋 [TextField onBlur] Called", { 
                      elementId: element.id, 
                      currentValue: formData[element.id], 
                      elementType 
                    });
                    handleDataElementBlur(element.id);
                  }}
                  helperText={helperText}
                  error={hasError}
                  variant="outlined"
                  inputProps={{
                    maxLength,
                    inputMode,
                  }}
                />
              );
            })}
          </Stack>
        </Box>
      )}

      {/* Purposes */}
      {orderedPurposes.length > 0 && (
        <Box sx={{ mb: 4 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2, color: "text.primary" }}>
            {t("consentPurposes")}
          </Typography>
          <FormGroup>
            {orderedPurposes.map((purpose) => {
              const hasError = !!fieldErrors[purpose.id];
              const errorMessage = fieldErrors[purpose.id];
              const isConsentPurpose = purpose.lawfulGround?.code === "CONSENT";

              return (
                <Paper
                  key={purpose.id}
                  variant="outlined"
                  sx={{
                    p: 2.5,
                    mb: 2,
                    borderColor: hasError ? "error.main" : "divider",
                    borderWidth: hasError ? 2 : 1,
                    "&:last-of-type": { mb: 0 },
                  }}
                >
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={purposeConsents[purpose.id] || false}
                        onChange={(e) => handlePurposeConsentChange(purpose.id, e.target.checked)}
                        required={purpose.isRequired && isConsentPurpose}
                        color={hasError ? "error" : "primary"}
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                          {getPurposeLabel(purpose)}
                        </Typography>
                        {getPurposeDescription(purpose) && (
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            {getPurposeDescription(purpose)}
                          </Typography>
                        )}
                        {formatRetentionSummary(purpose) && (
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, fontStyle: "italic" }}>
                            {t("retention")}: {formatRetentionSummary(purpose)}
                          </Typography>
                        )}
                        {hasError && (
                          <Typography variant="body2" color="error" sx={{ mt: 0.5, fontWeight: 500 }}>
                            {errorMessage}
                          </Typography>
                        )}
                      </Box>
                    }
                  />
                </Paper>
              );
            })}
          </FormGroup>
        </Box>
      )}

      {/* Policy links (Privacy Policy, Contact DPO, Your Rights) */}
      {subdomain && (
        <Paper variant="outlined" sx={{ mt: 3, p: 2, borderColor: "divider", bgcolor: "grey.50" }}>
          <Stack direction="row" flexWrap="wrap" spacing={2} useFlexGap alignItems="center">
            <Link
              href={`${config.forms.baseUrl.replace(/\/$/, "")}/${subdomain}/privacy-policy.html?lang=${uiLanguage}`}
              target="_blank"
              rel="noopener noreferrer"
              variant="body2"
              sx={{ fontWeight: 500 }}
            >
              {t("privacyPolicy")}
            </Link>
            <Link
              href={`${config.forms.baseUrl.replace(/\/$/, "")}/${subdomain}/contact-dpo.html?lang=${uiLanguage}`}
              target="_blank"
              rel="noopener noreferrer"
              variant="body2"
              sx={{ fontWeight: 500 }}
            >
              {t("contactDpo")}
            </Link>
            <Link
              href={`${config.forms.baseUrl.replace(/\/$/, "")}/${subdomain}/your-rights.html?lang=${uiLanguage}`}
              target="_blank"
              rel="noopener noreferrer"
              variant="body2"
              sx={{ fontWeight: 500 }}
            >
              {t("yourRights")}
            </Link>
          </Stack>
        </Paper>
      )}

      {/* Submit Button */}
      <Box sx={{ mt: 4, display: "flex", justifyContent: "flex-end" }}>
        <Button
          variant="contained"
          size="large"
          onClick={handleSubmit}
          disabled={submitting || formDisabled}
          sx={{ minWidth: 150, pointerEvents: "auto" }}
        >
          {submitting ? (
            <>
              <CircularProgress size={20} sx={{ mr: 1 }} />
              {t("loading")}...
            </>
          ) : (
            getLabelById(noticeConfig.submitButtonLabelId) || "Submit"
          )}
        </Button>
      </Box>
    </Box>
  );
};

export default ConsentNoticeForm;
